import React from 'react';
import clsx from 'clsx';

/**
 * Avatar Component
 * Professional avatar with initials fallback
 * Perfect for user profiles, participant lists, trainer cards
 */

const Avatar = ({
  src,
  alt,
  name,
  size = 'md',
  variant = 'circle',
  status,
  className,
  ...props
}) => {
  // Size styles
  const sizeStyles = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
    '2xl': 'w-20 h-20 text-2xl',
  };

  // Variant styles
  const variantStyles = {
    circle: 'rounded-full',
    rounded: 'rounded-lg',
    square: 'rounded-md',
  };

  // Status indicator size based on avatar size
  const statusSizeStyles = {
    xs: 'w-1.5 h-1.5 border',
    sm: 'w-2 h-2 border',
    md: 'w-2.5 h-2.5 border-2',
    lg: 'w-3 h-3 border-2',
    xl: 'w-3.5 h-3.5 border-2',
    '2xl': 'w-4 h-4 border-2',
  };

  // Status colors
  const statusColors = {
    online: 'bg-green-500',
    offline: 'bg-neutral-400',
    away: 'bg-amber-500',
    busy: 'bg-red-500',
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  // Generate background color from name
  const getBackgroundColor = (name) => {
    if (!name) return 'bg-neutral-200';

    const colors = [
      'bg-primary-100 text-primary-700',
      'bg-green-100 text-green-700',
      'bg-blue-100 text-blue-700',
      'bg-amber-100 text-amber-700',
      'bg-purple-100 text-purple-700',
      'bg-pink-100 text-pink-700',
      'bg-indigo-100 text-indigo-700',
      'bg-teal-100 text-teal-700',
    ];

    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const avatarClasses = clsx(
    'relative inline-flex items-center justify-center font-semibold overflow-hidden bg-neutral-100 text-neutral-600',
    sizeStyles[size],
    variantStyles[variant],
    !src && getBackgroundColor(name),
    className
  );

  return (
    <div className={avatarClasses} {...props}>
      {/* Avatar Image or Initials */}
      {src ? (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="select-none">{getInitials(name || alt)}</span>
      )}

      {/* Status Indicator */}
      {status && (
        <span
          className={clsx(
            'absolute bottom-0 right-0 rounded-full border-white',
            statusSizeStyles[size],
            statusColors[status]
          )}
        />
      )}
    </div>
  );
};

// Avatar Group - For displaying multiple avatars
Avatar.Group = ({ children, max = 3, size = 'md', className }) => {
  const childArray = React.Children.toArray(children);
  const displayedChildren = childArray.slice(0, max);
  const remainingCount = childArray.length - max;

  // Overlap offset based on size
  const overlapStyles = {
    xs: '-space-x-1',
    sm: '-space-x-2',
    md: '-space-x-2',
    lg: '-space-x-3',
    xl: '-space-x-3',
    '2xl': '-space-x-4',
  };

  return (
    <div className={clsx('flex items-center', overlapStyles[size], className)}>
      {displayedChildren.map((child, index) =>
        React.cloneElement(child, {
          key: index,
          size,
          className: clsx(child.props.className, 'ring-2 ring-white'),
        })
      )}

      {remainingCount > 0 && (
        <Avatar
          size={size}
          name={`+${remainingCount}`}
          className="ring-2 ring-white bg-neutral-200 text-neutral-600"
        />
      )}
    </div>
  );
};

export default Avatar;

/**
 * Usage Examples:
 *
 * // Basic Avatar with Image
 * <Avatar src="/path/to/image.jpg" alt="John Doe" />
 *
 * // Avatar with Initials (no image)
 * <Avatar name="John Doe" />
 *
 * // Avatar with Status
 * <Avatar
 *   name="Jane Smith"
 *   status="online"
 *   size="lg"
 * />
 *
 * // Different Sizes
 * <Avatar name="User" size="xs" />
 * <Avatar name="User" size="sm" />
 * <Avatar name="User" size="md" />
 * <Avatar name="User" size="lg" />
 * <Avatar name="User" size="xl" />
 * <Avatar name="User" size="2xl" />
 *
 * // Different Variants
 * <Avatar name="User" variant="circle" />
 * <Avatar name="User" variant="rounded" />
 * <Avatar name="User" variant="square" />
 *
 * // Avatar Group (Participant List)
 * <Avatar.Group max={5}>
 *   <Avatar name="John Doe" />
 *   <Avatar name="Jane Smith" />
 *   <Avatar name="Mike Johnson" />
 *   <Avatar name="Sarah Wilson" />
 *   <Avatar name="Tom Brown" />
 *   <Avatar name="Emily Davis" />
 * </Avatar.Group>
 *
 * // Trainer Avatar with Status
 * <Avatar
 *   src="/trainers/jane.jpg"
 *   name="Jane Smith"
 *   status="online"
 *   size="xl"
 * />
 */
